import { defineComponent as _defineComponent } from 'vue'
import {
  formatDateWithTimeOnCustomerTimeZone,
  timeSpanToStartOfUnitOfTime,
  timeSpanToUnitOfTime,
  userTimeZone,
} from '@/utils/dates';
import { FilterTimeSpanEnum } from '@/models/enums/FilterTimeSpanEnum';
import i18n from '@/i18n';
import { abbreviateNumber, formatPercentage } from '@/utils/number';
import { computed, watch } from 'vue';
import moment from 'moment-timezone/builds/moment-timezone-with-data-10-year-range.js';
import { TrendData } from '@/components/bend/kpiWidgetUtils';
import { isNil } from '@/utils/misc';
import { useI18n } from '@/composables/i18n';


export default /*#__PURE__*/_defineComponent({
  __name: 'KpiWidgetTrendTooltip',
  props: {
    trendData: null,
    position: { default: 'is-top' },
    invert: { type: Boolean, default: false },
    valueFormatter: { type: Function, default: abbreviateNumber }
  },
  setup(__props: any) {

const props = __props as { trendData: TrendData, position: 'is-top' | 'is-bottom' | 'is-left' | 'is-right', invert: boolean, valueFormatter: (n: number) => string };

// Here to avoid typing error from the IDE on unit tests
/**
 * Tooltip shown on a widget's trend to give the user more information about it.
 *
 * The information shown changes depending on the type of widget (pie/bar).
 *
 * Props:
 * - trendData: information about related to the trend to show to the user. It
 *   specifies the type of widget (pie/bar).
 * - position: where should the tooltip open:
 *  'is-top' (default), 'is-bottom', 'is-left', 'is-right'
 */



const { t } = useI18n();

let now = new Date();

watch(
  () => props.trendData,
  () =>
    // Widget refreshed, update to the new request date-time
    (now = new Date()),
);

function getCurrentTimePeriodDescription(timeSpan: FilterTimeSpanEnum): string {
  return i18n
    .t(`current_time_period.${FilterTimeSpanEnum[timeSpan]}`)
    .toString();
}

function getPreviousTimePeriodDescription(
  timeSpan: FilterTimeSpanEnum,
): string {
  return i18n
    .t(`previous_time_period.${FilterTimeSpanEnum[timeSpan]}`)
    .toString();
}

// Only for 'pie' type
function getCurrentDateTimeRange(): [Date, Date] {
  const unitOfTime = timeSpanToStartOfUnitOfTime(props.trendData.timeSpan!);
  const startOfRange = moment
    .tz(now, userTimeZone())
    .startOf(unitOfTime)
    .toDate();
  return [startOfRange, now];
}

// Only for 'pie' type
function getPreviousDateTimeRange(): [Date, Date] {
  const unitOfTime = timeSpanToUnitOfTime(props.trendData.timeSpan!);
  const startOfRange = moment
    .tz(getCurrentDateTimeRange()[0], userTimeZone())
    .subtract(1, unitOfTime)
    .toDate();
  const endOfRange = moment
    .tz(getCurrentDateTimeRange()[1], userTimeZone())
    .subtract(1, unitOfTime)
    .toDate();
  return [startOfRange, endOfRange];
}

const formattedDifferencePercentage = computed<string>(() =>
  formatPercentage(Math.abs(differencePercentage.value), 0),
);

const differencePercentage = computed<number>(() => {
  return (difference.value * 100) / props.trendData.previousValue;
});

const difference = computed<number>(
  () => props.trendData.currentValue - props.trendData.previousValue,
);

const differenceTrendClass = computed<string | undefined>(() => {
  if (props.trendData.previousValue === 0) {
    return undefined;
  }

  if (difference.value < 0) {
    return 'trend-down';
  } else if (difference.value > 0) {
    return 'trend-up';
  }

  return undefined;
});

return { __sfc: true,props, t, now, getCurrentTimePeriodDescription, getPreviousTimePeriodDescription, getCurrentDateTimeRange, getPreviousDateTimeRange, formattedDifferencePercentage, differencePercentage, difference, differenceTrendClass, formatDateWithTimeOnCustomerTimeZone, abbreviateNumber, isNil }
}

})